// ---------------------------------------------------------------------------
// The layouts that the templates of a page can draw.
//
// The layout policy of the code (data/layoutPolicy.js, libs/layoutOptions.ts)
// decides them per channel, stage, releases set and product. An admin can take
// some of them out of the Layout selector (`hiddenLayouts`,
// libs/selectorVisibility.ts) and put them in another order (`layoutOrders`),
// but cannot offer a layout that is not in this list: no template draws it.
//
// Pure module: the server imports it too (channel-settings.ts validates a
// layout change against it). Keep the imports relative.
// ---------------------------------------------------------------------------

import {layoutAmountFor} from "../data/products.js";
import {releasesSetsForProduct} from "../data/products/index.js";
import {resolveLayoutOptions, type LayoutOption} from "./layoutOptions";
import {
  defaultLayoutId,
  hiddenLayoutsOf,
  layoutOrderOf,
  orderedLayoutOptions,
  type ChannelSettingsLike,
} from "./selectorVisibility";

/**
 * The layout options of (dataChannel, type, set, product), the same list the
 * Toolbar offers before an admin hides any. No product reads the row of the
 * set for every product: the rule scope `*` of the Defaults panel.
 */
export function codeLayoutOptions({
  dataChannel,
  type,
  set,
  product,
}: {
  dataChannel: string;
  type: string | undefined;
  set?: string | null;
  product?: string | null;
}): LayoutOption[] {
  // Sets, and the layout row each set draws, belong to the releases offering.
  const sets: Array<{value: string; layoutIcons: string[]; layoutIconChannel?: string}> =
    dataChannel === "releases" ? releasesSetsForProduct(product ?? undefined) : [];
  const activeSet = sets.find((item) => item.value === set) ?? sets[0];
  return resolveLayoutOptions({
    channel: dataChannel,
    type,
    set: set ?? undefined,
    product: product ?? undefined,
    amount: layoutAmountFor(dataChannel, product),
    layoutIcons: activeSet?.layoutIcons,
  });
}

/** The Layout selector of a page, after the rules of an admin. */
export interface PageLayoutOffer {
  /** The layouts the templates draw, in the page's order. */
  options: LayoutOption[];
  /** The layouts that the selector does not offer. */
  hidden: Set<number>;
  /** The admin's order, or null when the code order applies. */
  order: number[] | null;
  /** The layout that code selects for the user (defaultLayoutId). */
  defaultId: number;
}

/**
 * The layouts of a page of (dataChannel, type, set, product) as the Layout
 * selector offers them: in the admin's order, without the hidden ones, and
 * the default layout. `settings` are the data channel's. `declared` is the
 * code's default layout: it applies while no order is saved.
 */
export function pageLayoutOffer({
  dataChannel,
  type,
  set,
  product,
  settings,
  declared,
}: {
  dataChannel: string;
  type: string | undefined;
  set?: string | null;
  product?: string | null;
  settings: ChannelSettingsLike;
  declared: number;
}): PageLayoutOffer {
  const order = layoutOrderOf(settings, product, set);
  const options = orderedLayoutOptions(
    codeLayoutOptions({dataChannel, type, set, product}),
    order
  );
  const hidden = hiddenLayoutsOf(settings, type, product, set);
  return {options, hidden, order, defaultId: defaultLayoutId(declared, options, hidden, order)};
}

/**
 * The `/Layouts/<channel>/` folder of the layout icons of (dataChannel, set,
 * product). A releases set that shows another channel's banners also shows
 * that channel's icons (the Emails set: the Email channel's).
 */
export function layoutIconChannelOf({
  dataChannel,
  set,
  product,
}: {
  dataChannel: string;
  set?: string | null;
  product?: string | null;
}): string {
  if (dataChannel !== "releases") return dataChannel;
  const sets: Array<{value: string; layoutIconChannel?: string}> = releasesSetsForProduct(
    product ?? undefined
  );
  const activeSet = sets.find((item) => item.value === set) ?? sets[0];
  return activeSet?.layoutIconChannel ?? dataChannel;
}
